<script lang="ts">
  import { marked } from "marked";
  import he from "he";
  import { sanitizeHtml } from "../utils/sanitize";
  import type { ChatMessage } from "../types/globalTypes";
  import PropertyCarousel from "./PropertyCarousel.svelte";
  import AttachmentMessage from "./AttachmentMessage.svelte";
  import UserIcon from "../utils/assets/user_icon.svelte";
  import { client_config } from "../stores/store";

  export let message: ChatMessage;
  export let index: number;
  export let isLastMessage: boolean = false;
  export let accentColor: string;
  export let activeAgentIndex: number[] = [];
  export let activeAssistantIndex: number[] = [];
  export let onQuickReplyClick: (text: string) => void;
  export let id: string;

  let showTranslation = false;
  let showPropertyTranslation = false;

  const userProfileIcon =
    $client_config?.user_profile_icon || "https://www.gravatar.com/avatar?d=mp";
  const clientLogoUrl =
    $client_config?.client_logo_url ||
    "https://vf-assets.verbaflo.com/logo.png";

  const formatMessageContent = (content: string) => {
    if (typeof content !== "string") {
      return "";
    }
    return (
      content?.replace(
        /\n+/g,
        (match) => "<br>".repeat(match?.length) + "\n".repeat(match?.length)
      ) || ""
    );
  };

  const formatTimestamp = (timestamp: string) => {
    return new Date(timestamp).toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  };

  const toggleTranslation = () => {
    showTranslation = !showTranslation;
  };

  const togglePropertyTranslation = () => {
    showPropertyTranslation = !showPropertyTranslation;
  };

  $: messageContent =
    showTranslation && message?.translated_message
      ? formatMessageContent(message.translated_message)
      : formatMessageContent(message.message);

  $: isBot = message.role === "assistant";
  $: isAgent = message.role === "agent";
  $: isUser = message.role === "user";

  $: hasMessage =
    message?.message && message?.message?.trim() !== "" ? true : false;

  $: showJoinMessage =
    (isBot && activeAssistantIndex.includes(index)) ||
    (isAgent && activeAgentIndex.includes(index));

  $: profileImage = isUser ? userProfileIcon : clientLogoUrl;
  $: displayName = isBot ? $client_config?.bot_name : message.agentName;

  $: hasTranslation = !isUser && !!message?.translated_message;
  $: hasPropertyTranslation =
    message?.property_json?.length > 0 &&
    message?.property_json?.[0]?.translated_amenities?.length > 0;

  $: hasProperties =
    message?.property_json &&
    Array.isArray(message?.property_json) &&
    message?.property_json?.length > 0;

  $: hasAttachments =
    message?.attachments &&
    Array.isArray(message?.attachments) &&
    message?.attachments?.length > 0;

  $: mainContainerGap = hasAttachments ? "4px" : "8px";

  $: messageBubbleWidth = hasAttachments ? "280px" : "100%";
  $: messageBubbleBottomRadius = hasAttachments ? "0px" : "8px";
  $: messageBubbleShadow = hasAttachments
    ? "none"
    : "0 1px 3px rgba(0, 0, 0, 0.1)";

  $: attachmentContainerMarginLeft =
    hasAttachments && hasMessage ? "32px" : "0px";
</script>

<div
  class="verbaflo-widget-message-container"
  style="--main-container-gap: {mainContainerGap};"
  class:verbaflo-widget-message-right-container={isUser}
>
  {#if showJoinMessage}
    <div class="verbaflo-widget-join-message-container">
      <hr />
      <p class="verbaflo-widget-join-message-text">
        <span class="verbaflo-widget-join-message-name">{displayName}</span>
        {$client_config?.joined_chat_label || "joined the chat"}
      </p>
      <hr />
    </div>
  {/if}

  {#if hasProperties}
    <div
      class="verbaflo-widget-property-container-wrapper"
      data-property-index={index}
    >
      <div class="verbaflo-widget-property-message-wrapper">
        <img
          src={clientLogoUrl}
          alt="Bot Profile"
          class="verbaflo-widget-profile-image"
        />
        <div class="verbaflo-widget-property-container">
          <PropertyCarousel
            {accentColor}
            {message}
            {showPropertyTranslation}
            {id}
          />
          {#if hasPropertyTranslation}
            <div class="verbaflo-widget-property-translation-container">
              <button
                class="verbaflo-widget-translation-text"
                style="--translate-btn-margin-top: 0px;"
                type="button"
                on:click={togglePropertyTranslation}
              >
                {showPropertyTranslation ? "See Original" : "See Translation"}
              </button>
            </div>
          {/if}
        </div>
      </div>
    </div>
  {/if}

  {#if hasMessage}
    <div
      class="verbaflo-widget-message-wrapper"
      class:verbaflo-widget-message-wrapper-reverse={isUser}
    >
      {#if isUser}
        <UserIcon />
      {:else}
        <img
          src={profileImage}
          alt={isUser ? "User Profile" : "Bot Profile"}
          class="verbaflo-widget-profile-image"
        />
      {/if}

      <div class="verbaflo-widget-message-content-wrapper">
        {#if !isUser}
          <div
            class="verbaflo-widget-message-bubble"
            style="--message-bubble-width: {messageBubbleWidth}; --message-bubble-bottom-radius: {messageBubbleBottomRadius}; --message-bubble-shadow: {messageBubbleShadow};"
          >
            <p class="verbaflo-widget-message-sender-name">{displayName}</p>
            <div class="verbaflo-widget-message-content-container">
              {#await marked.parse(messageContent) then parsedMessage}
                {@html sanitizeHtml(he
                  .decode(parsedMessage)
                  .replace(
                    /(?<!href="[^"]*?)(https?:\/\/[^\s<>"{}|\\^`\[\]]+(?:\s*[^\s<>"{}|\\^`\[\]]+)*)(?![^<]*?">)/g,
                    (match) => {
                      const cleanUrl = match.replace(/\s+/g, "");
                      return `<a href="${cleanUrl}" target="_blank" rel="noopener noreferrer" class="verbaflo-widget-message-link">${match}</a>`;
                    }
                  )
                  .replace(
                    /<a /g,
                    `<a target="_blank" rel="noopener noreferrer" class="verbaflo-widget-message-link"`
                  )
                  .replace(
                    /(?<!href="[^"]*?)(?<!https?:\/\/[^\s<>"{}|\\^`\[\]]*)(\+\d{1,3}[\s\-.]?)?(\(\d{1,4}\)[\s\-.]?)?((?:[0-9][\s\-.]?){6,}[0-9])(?![^<]*?">)(?!\s*[-–—]\s*\d{4})(?<!20\d{2}[-–—])(?<!19\d{2}[-–—])(?<!20\d{2}-\d{4})(?<!19\d{2}-\d{4})/g,
                    (match) => {
                      if (
                        /^\d{4}-\d{4}$/.test(match) ||
                        /^\d{4}-\d{3}$/.test(match) ||
                        /^\d{4}-\d{2}$/.test(match) ||
                        /^\d{4}-\d{1}$/.test(match)
                      ) {
                        return match;
                      }
                      return `<a href="tel:${match}" class="verbaflo-widget-message-link">${match}</a>`;
                    }
                  ))}
              {/await}
            </div>
          </div>
        {:else}
          <div
            class="verbaflo-widget-message-bubble-user"
            style="--accent-color: {accentColor};"
          >
            {#await marked.parse(typeof message?.message === "string" ? message?.message : "") then parsedMessage}
              {@html sanitizeHtml(he
                .decode(parsedMessage)
                .replace(
                  /<a /g,
                  '<a target="_blank" rel="noopener noreferrer" class="verbaflo-widget-message-link-user"'
                ))}
            {/await}
          </div>
        {/if}

        {#if isLastMessage && message.quick_replies && message.quick_replies.length > 0}
          <div class="verbaflo-widget-quick-replies">
            {#each message?.quick_replies as option}
              {#if option?.text}
                <button
                  style="--accent-color: {accentColor};"
                  on:click={() => onQuickReplyClick(option?.text)}
                >
                  {option?.text}
                </button>
              {/if}
            {/each}
          </div>
        {/if}

        {#if !hasAttachments}
          <div class="verbaflo-widget-message-bottom-container">
            <div
              class="verbaflo-widget-message-timestamp"
              class:verbaflo-widget-message-timestamp-right={isUser}
            >
              {formatTimestamp(message?.timestamp)}
            </div>
            {#if hasTranslation}
              <button
                class="verbaflo-widget-translation-text"
                on:click={toggleTranslation}
                type="button"
              >
                {showTranslation ? "See Original" : "See Translation"}
              </button>
            {/if}
          </div>
        {/if}
      </div>
    </div>
  {/if}

  {#if hasAttachments}
    <div
      class="verbaflo-widget-attachment-container"
      style="--attachment-container-margin-left: {attachmentContainerMarginLeft};"
    >
      {#if !hasMessage}
        {#if isUser}
          <UserIcon />
        {:else}
          <img
            src={profileImage}
            alt={isUser ? "User Profile" : "Bot Profile"}
            class="verbaflo-widget-profile-image"
          />
        {/if}
      {/if}
      <div style="width: 100%;">
        <AttachmentMessage
          {hasMessage}
          attachments={message?.attachments}
          timestamp={message?.timestamp}
        />
        <div class="verbaflo-widget-message-bottom-container">
          <div
            class="verbaflo-widget-message-timestamp"
            class:verbaflo-widget-message-timestamp-right={isUser}
          >
            {formatTimestamp(message?.timestamp)}
          </div>
          {#if hasTranslation}
            <button
              class="verbaflo-widget-translation-text"
              on:click={toggleTranslation}
              type="button"
            >
              {showTranslation ? "See Original" : "See Translation"}
            </button>
          {/if}
        </div>
      </div>
    </div>
  {/if}
</div>

<style>
  :global(#vfwidget .verbaflo-widget-message-container) {
    display: flex !important;
    flex-direction: column !important;
    max-width: 100% !important;
    gap: var(--main-container-gap, 8px) !important;
  }

  :global(#vfwidget .verbaflo-widget-message-right-container) {
    align-items: flex-end !important;
  }

  :global(#vfwidget .verbaflo-widget-join-message-container) {
    display: flex !important;
    align-items: center !important;
    gap: 12px !important;
    width: 100% !important;
    margin-bottom: 12px !important;
  }

  :global(#vfwidget .verbaflo-widget-join-message-container hr) {
    flex-grow: 1 !important;
    border: 1px solid rgba(0, 0, 0, 0.1) !important;
  }

  :global(#vfwidget .verbaflo-widget-message-sender-container) {
    display: flex !important;
    gap: 4px !important;
  }

  :global(#vfwidget .verbaflo-widget-profile-image) {
    width: 24px !important;
    height: 24px !important;
    border-radius: 50% !important;
    object-fit: cover !important;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2) !important;
  }

  :global(#vfwidget .verbaflo-widget-message-sender-name) {
    font-weight: 700 !important;
    color: #051133 !important;
    font-size: 14px !important;
    padding: 0 !important;
    margin: 0 !important;
    text-transform: none !important;
    line-height: 20px !important;
  }

  :global(#vfwidget .verbaflo-widget-message-bottom-container) {
    display: flex !important;
    justify-content: space-between !important;
    gap: 8px !important;
    align-items: center !important;
    width: fit-content !important;
    min-width: 100% !important;
  }

  :global(#vfwidget .verbaflo-widget-translation-text) {
    border: none !important;
    outline: none !important;
    box-shadow: none !important;
    background-color: transparent !important;
    padding: 0 !important;
    margin: 0 !important;
    color: #3ba4ff !important;
    font-size: 12px !important;
    text-transform: none !important;
    line-height: 16px !important;
    cursor: pointer !important;
    margin-top: var(--translate-btn-margin-top, 4px) !important;
  }

  :global(#vfwidget .verbaflo-widget-translation-text button) {
    max-width: fit-content !important;
    color: #3ba4ff !important;
    border: none !important;
    padding: 0 !important;
    font-size: 12px !important;
    font-weight: 400 !important;
    background: none !important;
    cursor: pointer !important;
    text-transform: none !important;
  }

  :global(#vfwidget .verbaflo-widget-translation-text:hover) {
    background-color: transparent !important;
    color: #3ba4ff !important;
    opacity: 1 !important;
    text-decoration: none !important;
  }

  :global(#vfwidget .verbaflo-widget-translation-text:active) {
    background-color: transparent !important;
    color: #3ba4ff !important;
    opacity: 1 !important;
    text-decoration: none !important;
  }

  :global(#vfwidget .verbaflo-widget-join-message-text),
  :global(#vfwidget .verbaflo-widget-join-message-name) {
    color: #737373 !important;
    font-size: 14px !important;
    font-weight: 400 !important;
    white-space: nowrap !important;
    margin: 0 !important;
    padding: 0 !important;
    max-width: 80% !important;
    text-wrap: wrap !important;
    text-align: center !important;
  }

  :global(#vfwidget .verbaflo-widget-message-wrapper) {
    display: flex !important;
    align-items: flex-start !important;
    gap: 8px !important;
    width: fit-content !important;
    max-width: 100% !important;
  }

  :global(#vfwidget .verbaflo-widget-message-wrapper-reverse) {
    flex-direction: row-reverse !important;
  }

  :global(#vfwidget .verbaflo-widget-property-message-wrapper) {
    display: flex !important;
    align-items: flex-start !important;
    gap: 8px !important;
    width: 100% !important;
  }

  :global(#vfwidget .verbaflo-widget-message-content-wrapper) {
    display: flex !important;
    flex-direction: column !important;
    gap: 4px !important;
    max-width: 280px !important;
  }

  :global(#vfwidget .verbaflo-widget-message-bubble) {
    background-color: #f6f6f6 !important;
    border-radius: 8px !important;
    border-bottom-left-radius: 0 !important;
    border-bottom-right-radius: var(
      --message-bubble-bottom-radius,
      8px
    ) !important;
    box-shadow: var(--message-bubble-shadow) !important;
    padding: 10px !important;
    width: var(--message-bubble-width, 100%) !important;
    max-width: 280px !important;
  }

  :global(#vfwidget .verbaflo-widget-message-content-container *) {
    color: black !important;
    font-size: 14px !important;
    font-weight: 400 !important;
    margin: 0 !important;
    padding: 0 !important;
    line-height: normal !important;
  }

  :global(#vfwidget .verbaflo-widget-message-content-container > p) {
    letter-spacing: normal !important;
  }

  :global(#vfwidget .verbaflo-widget-message-content-container strong) {
    font-weight: 700 !important;
  }

  :global(#vfwidget .verbaflo-widget-message-content-container b) {
    font-weight: 700 !important;
  }

  :global(#vfwidget .verbaflo-widget-message-content-container em) {
    font-style: italic !important;
  }

  :global(#vfwidget .verbaflo-widget-message-content-container i) {
    font-style: italic !important;
  }

  :global(#vfwidget .verbaflo-widget-message-content-container a) {
    text-decoration: underline !important;
    color: #0000ee !important;
  }

  :global(#vfwidget .verbaflo-widget-message-content-container ol) {
    list-style-type: decimal !important;
    padding: 0 16px !important;
    margin-top: 8px !important;
  }

  :global(#vfwidget .verbaflo-widget-message-content-container ul) {
    list-style-type: disc !important;
    padding: 0 16px !important;
    margin-top: 4px !important;
  }

  :global(#vfwidget .verbaflo-widget-message-link) {
    text-decoration: underline !important;
  }

  :global(#vfwidget .verbaflo-widget-quick-replies) {
    display: flex !important;
    flex-wrap: wrap !important;
    gap: 8px !important;
    margin-top: 8px !important;
    width: fit-content !important;
    min-width: 100% !important;
  }

  :global(#vfwidget .verbaflo-widget-quick-replies button) {
    max-width: fit-content !important;
    color: var(--accent-color) !important;
    border: 1px solid var(--accent-color) !important;
    border-radius: 1000px !important;
    padding: 4px 8px !important;
    font-size: 12px !important;
    font-weight: 400 !important;
    background: none !important;
    cursor: pointer !important;
    text-transform: none !important;
    height: auto !important;
    min-height: 0 !important;
    line-height: 1.2 !important;
    box-sizing: border-box !important;
    vertical-align: middle !important;
  }

  :global(#vfwidget .verbaflo-widget-quick-replies button:hover) {
    background-color: var(--accent-color) !important;
    color: #ffffff !important;
    opacity: 1 !important;
    text-decoration: none !important;
  }

  :global(#vfwidget .verbaflo-widget-quick-replies button:active) {
    background-color: var(--accent-color) !important;
    color: #ffffff !important;
    opacity: 1 !important;
    text-decoration: none !important;
  }

  :global(#vfwidget .verbaflo-widget-message-bubble-user) {
    background-color: var(--accent-color) !important;
    border-radius: 8px !important;
    border-bottom-right-radius: 0 !important;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1) !important;
    padding: 10px !important;
    max-width: 280px !important;
    word-break: break-word !important;
  }

  :global(#vfwidget .verbaflo-widget-message-bubble-user *) {
    color: white !important;
    font-size: 14px !important;
    font-weight: 400 !important;
    margin: 0 !important;
    padding: 0 !important;
    text-transform: none !important;
  }

  :global(#vfwidget .verbaflo-widget-message-link-user) {
    color: white !important;
    text-decoration: underline !important;
  }

  :global(#vfwidget .verbaflo-widget-message-timestamp) {
    color: #737373 !important;
    font-size: 12px !important;
    font-weight: 400 !important;
    text-align: left !important;
    margin-top: 4px !important;
    text-transform: uppercase !important;
    white-space: nowrap !important;
  }

  :global(#vfwidget .verbaflo-widget-message-timestamp-right) {
    text-align: right !important;
  }

  :global(#vfwidget .verbaflo-widget-property-container) {
    display: flex !important;
    flex-direction: column !important;
    align-items: flex-start !important;
    gap: 8px !important;
    width: 90% !important;
  }

  :global(#vfwidget .verbaflo-widget-property-container-wrapper) {
    display: flex !important;
    flex-direction: column !important;
    align-items: flex-end !important;
    width: 100% !important;
  }

  :global(#vfwidget .verbaflo-widget-property-translation-container) {
    display: flex !important;
    justify-content: flex-end !important;
    width: 100% !important;
  }

  :global(#vfwidget .verbaflo-widget-attachment-container) {
    display: flex !important;
    align-items: flex-start !important;
    gap: 8px !important;
    width: 280px !important;
    margin-left: var(--attachment-container-margin-left, 0px) !important;
  }
</style>
